<template>
	<div class="card-details-wrapper">
		<div class="card-details-container">
			<div class="row-content">
				<div class="col-carousel">
					<GalleryCarousel :slides="slides" />
				</div>
				<div class="row-details">
					<div class="heading-content">
						<h3>{{ card.title }}</h3>
						<p>{{ card.subtitle }}</p>
						<div v-if="!card?.downloadLinks" class="review-items">
							<div class="review-item">
								<div v-if="card.reviews.length > 0" class="align-rating">
									<img :src="reviewImage(this.averageTotalReview)" alt="rating star">
									<p class="rating">{{ averageTotalReview.toFixed(1) }}</p>
								</div>
							</div>
						</div>
					</div>
					<div class="prices-row">
						<div class="prices-align">
							<p class="price-strike" v-if="shopStore.currencyItemPrice(card, true)">{{ currencyStore.readableCurrencyValue(shopStore.currencyItemPrice(card, true), false) }}</p>
							<p>{{ currencyStore.readableCurrencyValue(shopStore.currencyItemPrice(card), false) }} <span>{{ getString('plus_vat') }}</span></p>
						</div>
						<AppNumberInput v-if="!card?.hideAddToCartBtn" :min="0" :value="shopStore.getItemQuantity(card)" @changed-value="newValue => shopStore.changeItemQuantity(card, newValue)" />
					</div>
					<AppButton v-if="!card?.hideAddToCartBtn" :text="getString('add')" @click="addItemAndOpenCart" />
					<div class="description-container">
						<p :class="{'small': card?.downloadLinks}" v-html="card.description"></p>
					</div>
					<div v-if="card.chips && card.chips.length" class="product-chips">
						<span class="chip" v-for="(chip, i) in card.chips" :key="i">
							<img :src="chip.icon" alt="">{{ chip.label }}
						</span>
					</div>
					<div v-if="!card?.downloadLinks" class="delivery-container">
						<img src="https://img.qromo.io/img/icon-shipping.svg"  alt="icon delivery">
						<div class="delivery-content">
							<p class="heading-delivery">{{ getString('delivery_label') }}</p>
							<p class="heading-sub-delivery">{{ getString('free_delivery') }}</p>
							<p class="heading-time-delivery">{{ getString('management_time') }}</p>
							<p class="content-delivery">{{ getString('standard_delivery_min') }}<span>{{ getString('standard_delivery_continue_min', [formattedShippingCost(true)]) }}</span></p>
							<p class="content-delivery">{{ getString('express_delivery_min') }}<span>{{ getString('express_delivery_continue_min', [formattedShippingCost(false)]) }}</span></p>
						</div>
					</div>
					<div v-else-if="card?.downloadLinks" class="delivery-container">
						<img src="https://img.qromo.io/img/icon-download.svg"  alt="icon download">
						<div class="delivery-content">
							<p class="heading-delivery">{{ getString('stores_available') }}</p>
							<p class="heading-sub-delivery">{{ getString('free_download_app') }}</p>
							<div class="row flex-start stores-download-container">
								<a v-if="card.downloadLinks?.android" target="_blank" :href="card.downloadLinks.android">
									<img :src="'https://img.qromo.io/img/play-store-badge'+linkSuffix+'.svg'" alt="Android download">
								</a>
								<a v-if="card.downloadLinks?.ios" target="_blank" :href="card.downloadLinks.ios">
									<img :src="'https://img.qromo.io/img/app-store-badge'+linkSuffix+'.svg'" alt="iOS download">
								</a>
							</div>
						</div>
					</div>
					<div v-if="!card?.downloadLinks" class="row-installment">
						<p>{{ getString("installments") }}</p>
						<img src="https://img.qromo.io/img/logo-klarna.svg" alt="logo klarna">
					</div>
				</div>
			</div>
			<div v-if="card.techSpec" class="row-tec-details">
				<div class="title-details">
					<p>{{ getString('technical_specifications') }}</p>
				</div>
				<div class="container-tec-items">
					<div class="col-tec-item" v-for="(techSpec, index) in card.techSpec" :key="index">
						<p class="title-tec-item">{{ techSpec.title }}</p>
						<p>{{ techSpec.details }}</p>
					</div>
				</div>
			</div>
			<div v-if="card.techSpecSections" class="row-tec-details">
				<div class="title-details">
					<p>{{ getString('technical_specifications') }}</p>
				</div>
				<div v-for="(section, sIndex) in card.techSpecSections" :key="sIndex" class="section-tec-details">
					<p class="title-tec-section">{{ section.title }}</p>
					<div class="container-tec-items">
						<div class="col-tec-item" v-for="(techSpec, index) in section.specs" :key="index">
							<p class="title-tec-item">{{ techSpec.title }}</p>
							<p>{{ techSpec.details }}</p>
						</div>
					</div>
				</div>
			</div>
			<div v-if="card.faqs?.length" class="container-faq">
				<div class="heading-section">
					<img :src="'https://img.qromo.io/img/icon-faq'+(userStore.darkMode ? '-dark' : '')+'.svg'" alt="icon faq">
					<h3>{{ getString('faq') }}</h3>
				</div>
				<div class="row-faq">
					<div  v-for="(faq, index) in card.faqs" :key="index" class="faq">
						<div @click="toggleFaq(index)" class="card-header-container tickets-container row centered space-between">
							<div class="title"><h2>{{ faq.request }}</h2></div>
							<AppChevronIcon :direction="faq.active ? 'up' : 'down'" />
						</div>
						<AppDropdown squareCorner :collapsed="!faq.active">
							<p>{{ faq.answer }}</p>
						</AppDropdown>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import GalleryCarousel from "@/shop/components/GalleryCarousel.vue";
import CartShop from "@/shop/components/CartShop.vue";
import {useShopStore} from "@/shop/stores";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import {useUserStore} from "@/console-new/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const shopStore = useShopStore();
		let userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {currencyStore, languageStore, getString: languageStore.getString, shopStore, userStore};
	},
	components: {AppDropdown, CartShop, AppNumberInput, AppButton, GalleryCarousel, AppChevronIcon},
	name: "CardDetails",
	props: {
		card: {
			type: Object,
			required: true,
			validator(value) { return value && value.id && value.title && value.subtitle && value.description && value.slides && value.imagemin && value.image && value.reviews; },
		},
	},
	computed: {
		slides() {
			return this.card.slides.map((slide, i) => {
				return {
					id: i,
					url: "https://img.qromo.io/img/"+slide+".jpg",
					urlMin: "https://img.qromo.io/img/"+slide+".webp",
				}
			});
		},
		averageTotalReview() {
			return this.card.reviews.reduce((acc, curr) => acc + curr.rating, 0)/this.card.reviews.length;
		},
		linkSuffix() {
			return this.languageStore.lang !== 'it' ? "-en" : "";
		},
	},
	methods: {
		formattedShippingCost(low) {
			let costPart = low ? 'low' : 'high';
			let finalPrice = this.shopStore.currencyItemPrice({id: "shipping_"+costPart});
			return this.currencyStore.readableCurrencyValue(finalPrice, false);
		},
		addItemAndOpenCart() {
			this.shopStore.addItemToCart(this.card);
			this.shopStore.showCart = true;
		},
		toggleFaq(index) {
			this.card.faqs[index].active = !this.card.faqs[index].active
			this.$forceUpdate();
		},
		reviewImage(reviewValue) {
			const baseUrl = "https://img.qromo.io/img/";
			if(reviewValue<= 0.25) {
				return baseUrl+"icon-star-rating-0.svg"
			} else if(reviewValue<= 0.75) {
				return baseUrl+"icon-star-rating-1.svg"
			} else if(reviewValue<= 1.25) {
				return baseUrl+"icon-star-rating-2.svg"
			} else if(reviewValue<= 1.75) {
				return baseUrl+"icon-star-rating-3.svg"
			} else if(reviewValue<= 2.25) {
				return baseUrl+"icon-star-rating-4.svg"
			} else if(reviewValue<= 2.75) {
				return baseUrl+"icon-star-rating-5.svg"
			} else if(reviewValue<= 3.25) {
				return baseUrl+"icon-star-rating-6.svg"
			} else if(reviewValue<= 3.75) {
				return baseUrl+"icon-star-rating-7.svg"
			} else if(reviewValue<= 4.25) {
				return baseUrl+"icon-star-rating-8.svg"
			} else if(reviewValue<= 4.75) {
				return baseUrl+"icon-star-rating-9.svg"
			} else {
				return baseUrl+"icon-star-rating-10.svg"
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.card-details-wrapper {
	display: flex;
	margin: 0 auto;
	overflow: hidden;
	padding: 20px;
	@media(max-width: 420px) {
		padding: 20px 10px;
	}
	@media(max-width: 300px) {
		padding: 20px 10px;
	}
	.card-details-container {
		width: 100%;

		.row-content {
			display: flex;
			justify-content: space-between;
			max-width: 1260px;
			margin: 0 auto;
			@media(max-width: 768px) {
				flex-direction: column;
			}
		}
		.row-details {
			display: flex;
			flex-direction: column;
			max-width: 40%;
			flex: 0 0 40%;
			padding: 0 0 0 20px;
			margin-left: auto;
			height: fit-content;
			@media(max-width: 1260px) {
				padding: 0 0 0 20px;
			}
			@media(max-width: 768px) {
				max-width: 500px;
				width: 100%;
				flex: 0 0 100%;
				padding: 0;
				margin: 40px auto 0;
			}
			.prices-row {
				display: flex;
				justify-content: space-between;
				align-items: center;
				margin-bottom: 15px;
				.prices-align {
					display: flex;
					flex-wrap: wrap;
					gap: 5px;
					max-width: calc(100% - 120px);
					p {
						text-align: left;
						font: bold 16px/11px Montserrat;
						&.price-strike {
							color: #E24444;
							position: relative;
							margin-right: 8px;
							font-size: 14px;
							font-weight: normal;
							&:after {
								content: "";
								position: absolute;
								top: 38%;
								left: 0;
								width: 100%;
								height: 2px;
								background-color: #E24444;
							}
						}
						span {
							text-align: left;
							font: 12px/12px Montserrat;
							color: var(--color-txt-1);
						}
					}
				}
			}
			.heading-content {
				h3 {
					text-align: left;
					font: bold 16px/19px Montserrat;
					color: var(--color-txt);
				}
				p {
					text-align: left;
					font: 12px/15px Montserrat;
					color: var(--color-txt-1);
				}
				margin-bottom: 15px;
			}
			.product-chips {
				display: flex;
				gap: 6px;
				flex-wrap: wrap;
				margin-bottom: 20px;
				.chip {
					display: inline-flex;
					align-items: center;
					gap: 4px;
					padding: 2px 7px;
					border: 1px solid var(--color-secondary);
					border-radius: 20px;
					font-size: 10px;
					font-weight: 500;
					color: var(--color-txt-1);
					background: #2debbf1a;
					img {
						width: 12px;
						height: 12px;
					}
				}
			}
			.description-container {
				border-top: 1px solid var(--color-border-alpha);
				border-bottom: 1px solid var(--color-border-alpha);
				margin: 20px 0;
				padding: 20px 0;

				p.small {
					font-size: 14px;
					line-height: 16px;
					@media(max-width: 550px) {
						font-size: 12px;
					}
				}
			}
			.delivery-container {
				display: flex;
				align-items: flex-start;
				border-bottom: 1px solid var(--color-border-alpha);
				margin: 0 0 20px 0;
				img {
					width: 20px;
				}
				.delivery-content {
					margin-left: 10px;
					.heading-delivery {
						font-weight: 500;
					}
					.heading-sub-delivery {
						font-size: 14px;
					}
					.heading-time-delivery {
						font-size: 12px;
						line-height: 14px;
						margin-bottom: 30px;
					}
					.content-delivery {
						font-weight: 500;
						margin-bottom: 15px;
						font-size: 14px;
						span {
							font-size: 14px;
							color: var(--color-txt-2);
							margin-left: 6px;
						}
					}
				}

				.stores-download-container {
					padding-top: 20px;
					padding-bottom: 20px;
					gap: 20px;
					a {
						text-decoration: none;
						&:after {
							display: none;
						}
					}
					img	{
						height: 34px;
						width: unset;
					}
				}
			}
			:deep(.base-button) {
				width: 100%;
				max-width: unset;
				padding: 8px 22px;
			}
			.review-items {
				margin-top: 10px;
				display: flex;
				flex-direction: column;
				p {
					font: 12px/15px Montserrat;
					color: $txt_0;
				}
				.align-rating {
					display: flex;
					margin-bottom: 8px;
					img {
						width: 100px;
						height: auto;
					}
					p {
						font: bold 16px/16px Montserrat;
						margin-left: 10px;
					}
				}
			}
			.row-installment {
				display: flex;
				img {
					max-width: 50px;
					margin-left: 10px;
				}
			}
		}
		.row-tec-details {
			padding: 20px;
			margin-top: 40px;
			background-color: var(--color-bg-primary-2);
			border-radius: 13px;
			.title-details {
				margin-bottom: 15px;
				p {
					text-align: left;
					font: normal normal bold 14px/18px Montserrat;
					color: var(--color-txt);
				}
			}
			.container-tec-items {
				display: flex;
				flex-wrap: wrap;
				position: relative;
				&:after {
					content: '';
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					border-left: 1px solid var(--color-border-alpha);
					border-top: 1px solid var(--color-border-alpha);
					border-right: 1px solid var(--color-border-alpha);
					border-bottom: 1px solid var(--color-border-alpha);
					box-sizing: border-box;
				}
				.col-tec-item {
					display: flex;
					flex-direction: column;
					flex: 0 0 20%;
					max-width: 20%;
					padding: 10px 15px;
					border: 0.5px solid var(--color-border-alpha);
					@media(max-width: 1440px) {
						flex: 0 0 25%;
						max-width: 25%;
					}
					@media(max-width: 1260px) {
						flex: 0 0 33.333%;
						max-width: 33.333%;
					}
					@media(max-width: 768px) {
						flex: 0 0 50%;
						max-width: 50%;
					}
					@media(max-width: 450px) {
						flex: 0 0 100%;
						max-width: 100%;
					}
					p {
						text-align: left;
						font: 10px/13px Montserrat;
						color: var(--color-txt);
						&.title-tec-item {
							margin-bottom: 5px;
							font: bold 10px/13px Montserrat;
						}
					}
				}
			}
			.section-tec-details {
				margin-bottom: 15px;
				&:last-child {
					margin-bottom: 0;
				}
				&:not(:first-child) {
					margin-top: 5px;
				}
				.title-tec-section {
					text-align: left;
					font: bold 12px/16px Montserrat;
					color: var(--color-txt-1);
					margin-bottom: 5px;
				}
			}
		}
		.heading-section {
			position: relative;
			margin-top: 40px;
			padding: 0 20px;
			display: flex;
			align-items: center;
			&:after {
				content: '';
				position: absolute;
				bottom: 0;
				left: 20px;
				width: calc(100% - 40px);
				height: 1px;
				background-color: var(--color-bg);
				@media(max-width: 520px) {
					left: 0;
					width: 100%;
				}
			}
			@media(max-width: 768px) {
				margin-top: 30px;
			}
			@media(max-width: 520px) {
				padding: 0;
				margin-top: 25px;
			}
			h3 {
				padding: 10px 0;
				color: var(--color-txt);
				font-size: 14px;
			}
			img {
				margin-right: 5px;
				width: 23px;
				height: 23px;
			}
		}
		.container-review {
			.row-review {
				display: flex;
				gap: 20px;
				padding: 20px 3px;
				margin: 20px 20px 0;
				overflow-x: auto;
				@media(max-width: 520px) {
					gap: 10px;
					margin: 10px 0 0;
					padding: 20px 2px;
				}
				.wrapper-card-review {
					background-color: var(--color-bg);
					border-radius: 13px;
					box-shadow: 0 2px 3px rgba(7, 82, 163, 0.3);
					padding: 20px;
					flex: 0 0 33.333%;
					display: flex;
					flex-direction: column;
					max-width: calc(33.333% - 14px);
					min-width: 320px;
					width: 100%;
					@media(max-width: 420px) {
							min-width: 290px;
					}
					@media(max-width: 340px) {
						min-width: 270px;
					}
					@media(max-width: 320px) {
						min-width: 250px;
					}
					.row-heading {
						display: flex;
						align-items: center;
						justify-content: space-between;
						margin-bottom: 5px;
						h3 {
							font-size: 16px;
							color: var(--color-txt);
						}
						p {
							font-size: 12px;
							line-height: 16px;
						}
					}
					.row-vertical {
						img {
							width: 100px;
							height: auto;
							@media(max-width: 340px) {
								width: 80px;
							}
						}
						p {
							margin-top: 10px;
							border-top: 1px solid var(--color-border-alpha);
							padding-top: 10px;
						}
					}
				}
			}
		}
		.container-faq {
			.row-faq {
				margin-top: 20px;
				padding: 0 20px;
				@media(max-width: 520px) {
					padding: 0;
				}
				.faq {
					margin-bottom: 10px;
					border-bottom: 1px solid var(--color-border-alpha);
					.card-header-container {
						width: 100%;
						background-color: var(--color-bg);
						border-radius:0;
						padding: 10px;
						text-align: left;
						display: flex;
						justify-content: space-between;
						align-items: center;

						&.tickets-container {
							@include noSelect;
							cursor: pointer;
							.title { width: calc(100% - 20px); }
							.chevron { width: 50px; }
						}

						h2 {
							font-size: 16px;
							font-style: normal;
							font-weight: normal;
						}

						&.feedbacks-container {
							@include noSelect;
							align-items: center;
							justify-content: space-between;
							cursor: pointer;
							.title { width: calc(100% - 150px); }
							.stars { width: 150px; }
						}
					}
				}
			}
			.heading-section {
				margin-top: 30px;
				@media(max-width: 768px) {
					margin-top: 25px;
				}
				@media(max-width: 520px) {
					padding: 0;
					margin-top: 15px;
				}
			}
		}
		.col-carousel {
			width: calc(60% - 20px);
			padding: 20px;
			border-radius: 13px;
			box-shadow: 0 3px 5px var(--color-box-shadow-3);
			display: flex;
			flex-direction: column;
			max-width: 700px;
			flex: 0 0 60%;
			height: fit-content;
			background-color: var(--color-bg);
			@media(max-width: 768px) {
				width: 100%;
				flex: 0 0 100%;
				margin: 0 auto;
				max-width: 500px;
			}
			@media(max-width: 520px) {
				padding: 15px;
			}
		}
	}
}
</style>